import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  savePkceParams,
  computeRedirectUri,
} from './pkce'

import { getApiBase, getLogoutRedirectUrl } from '@/lib/deployment'

const TOKEN_KEY  = 'aip_dns_access_token'
const SILENT_KEY = 'aip_dns_silent_attempt'
const SKIP_AUTH  = import.meta.env.VITE_SKIP_AUTH === 'true'
const API_BASE   = getApiBase()

export interface IdpConfig {
  authorization_endpoint: string
  token_endpoint: string
  client_id: string
  scopes: string
  redirect_uri: string
}

export interface AuthContextValue {
  token: string | null
  isAuthenticated: boolean
  idpConfig: IdpConfig
  idpConfigLoading: boolean
  idpConfigError: string | null
  login: () => Promise<void>
  loginSilent: () => Promise<void>
  logout: () => void
  setToken: (token: string) => void
}

// Env vars are optional overrides — they take precedence over backend /auth/info values.
// If not set, the backend's OIDC discovery fills them in automatically.
function envIdpConfig(): IdpConfig {
  return {
    authorization_endpoint: import.meta.env.VITE_IDP_AUTHORIZATION_URL || '',
    token_endpoint:         import.meta.env.VITE_IDP_TOKEN_URL         || '',
    client_id:              import.meta.env.VITE_IDP_CLIENT_ID         || '',
    scopes:                 import.meta.env.VITE_IDP_SCOPES            || 'openid profile',
    redirect_uri: computeRedirectUri(),
  }
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function buildRedirectUrl(
  config: IdpConfig,
  extraParams: Record<string, string> = {},
): Promise<string> {
  const verifier  = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  const state     = generateState()
  savePkceParams(verifier, state)

  const url = new URL(config.authorization_endpoint)
  url.searchParams.set('response_type',         'code')
  url.searchParams.set('client_id',             config.client_id)
  url.searchParams.set('redirect_uri',          config.redirect_uri)
  url.searchParams.set('scope',                 config.scopes)
  url.searchParams.set('code_challenge',        challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('state',                 state)
  for (const [k, v] of Object.entries(extraParams)) url.searchParams.set(k, v)
  return url.toString()
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY)
  )
  const [idpConfig, setIdpConfig]             = useState<IdpConfig>(envIdpConfig)
  const [idpConfigLoading, setIdpConfigLoading] = useState(!SKIP_AUTH)
  const [idpConfigError, setIdpConfigError]   = useState<string | null>(null)

  // Bootstrap IDP config from backend /auth/info (OIDC discovery).
  // Env vars take precedence — backend fills in any fields left empty.
  // This makes the UI IDP-agnostic: just set IDP_ISSUER on the backend.
  useEffect(() => {
    if (SKIP_AUTH) return

    const load = async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/info`)
        if (!resp.ok) {
          setIdpConfigError(`Backend /auth/info returned ${resp.status}`)
          return
        }
        const info = await resp.json()
        if (!info.ok) {
          setIdpConfigError(info.error || 'IDP is not configured on the server')
          return
        }
        setIdpConfig(prev => ({
          // Env var wins if set; backend value fills the gap otherwise
          authorization_endpoint: prev.authorization_endpoint || info.authorization_endpoint || '',
          token_endpoint:         prev.token_endpoint         || info.token_endpoint         || '',
          client_id:              prev.client_id              || info.client_id              || '',
          scopes:                 prev.scopes                 || info.scopes                 || 'openid profile',
          redirect_uri:           prev.redirect_uri,
        }))
      } catch {
        // Network error — only fatal when env vars are also absent
        const env = envIdpConfig()
        if (!env.authorization_endpoint || !env.client_id) {
          setIdpConfigError('Cannot reach the backend and no IDP env vars are configured')
        }
      } finally {
        setIdpConfigLoading(false)
      }
    }

    load()
  }, [])

  const setToken = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    setTokenState(newToken)
  }, [])

  const login = useCallback(async () => {
    sessionStorage.removeItem(SILENT_KEY)
    window.location.href = await buildRedirectUrl(idpConfig)
  }, [idpConfig])

  const loginSilent = useCallback(async () => {
    sessionStorage.setItem(SILENT_KEY, '1')
    window.location.href = await buildRedirectUrl(idpConfig, { prompt: 'none' })
  }, [idpConfig])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(SILENT_KEY)
    sessionStorage.removeItem('aip_dns_silent_failed')
    setTokenState(null)
    window.location.href = getLogoutRedirectUrl()
  }, [])

  const isAuthenticated = SKIP_AUTH || token !== null

  return (
    <AuthContext.Provider value={{
      token, isAuthenticated,
      idpConfig, idpConfigLoading, idpConfigError,
      login, loginSilent, logout, setToken,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function isSilentAttempt(): boolean {
  return sessionStorage.getItem(SILENT_KEY) === '1'
}

export function clearSilentAttempt(): void {
  sessionStorage.removeItem(SILENT_KEY)
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
